import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useLikes } from '@/hooks/useLikes';
import { useSessionTracking } from '@/hooks/useSessionTracking';
import { useSubscriptions } from '@/hooks/useSubscriptions';
import { useViewCount } from '@/hooks/useViewCount';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { Heart, MessageCircle, Bell, BellOff } from 'lucide-react';
import { sanitizeText } from '@/lib/validations';
import AuthModal from '@/components/AuthModal';


interface Comment {
  id: string;
  content: string;
  author_name: string | null; // Keep for backward compatibility with old guest comments
  user_id: string;
  parent_comment_id: string | null;
  created_at: string;
  profiles?: {
    display_name: string;
  } | null;
  replies?: Comment[];
}

interface CommentSectionProps {
  blogPostId: string;
}

const CommentItem = ({ 
  comment, 
  onReply, 
  blogPostId,
  onAuthRequired,
  isNestedReply = false,
  lastViewTime
}: { 
  comment: Comment; 
  onReply: (commentId: string) => void;
  blogPostId: string;
  onAuthRequired: () => void;
  isNestedReply?: boolean;
  lastViewTime: Date | null;
}) => {
  const { user } = useAuth();
  const { sessionKey } = useSessionTracking();
  const [authorName, setAuthorName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const { likeCount, isLiked, toggleLike } = useLikes(comment.id, 'comment');

  useEffect(() => {
    const fetchAuthorName = async () => {
      if (comment.user_id) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('display_name, avatar_url_80, avatar_url_200, avatar_url')
          .eq('user_id', comment.user_id)
          .single();
        
        setAuthorName(profile?.display_name || 'User');
        setAvatarUrl(profile?.avatar_url_80 || profile?.avatar_url_200 || profile?.avatar_url || null);
      } else {
        setAuthorName(comment.author_name || 'Anonymous');
      }
    };

    fetchAuthorName();
  }, [comment]);

  // Check if comment is new (created after user's last view)
  const isNewComment = lastViewTime && new Date(comment.created_at) > lastViewTime;

  return (
    <div 
      id={`comment-${comment.id}`} 
      className={`space-y-3 ${isNewComment ? 'bg-blue-50/50 dark:bg-blue-950/20 border-l-2 border-l-blue-500 pl-3 -ml-3 py-2' : ''}`}
    >
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center gap-3">
          <Avatar className="w-8 h-8">
            <AvatarImage src={avatarUrl || undefined} alt={authorName} />
            <AvatarFallback>{authorName[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="font-medium">{authorName}</div>
        </div>
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
          {isNewComment && (
            <Badge variant="secondary" className="text-xs">New</Badge>
          )}
        </div>
      </div>
      <p className="text-sm leading-relaxed">{comment.content}</p>
      
      <div className="flex items-center gap-4 text-sm">
        <button
          onClick={() => toggleLike(sessionKey)}
          className={`flex items-center gap-1 hover:text-red-500 transition-colors ${
            isLiked ? 'text-red-500' : 'text-muted-foreground'
          }`}
        >
          <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
          {likeCount > 0 && <span>{likeCount}</span>}
        </button>
        
        {!isNestedReply && (
          <button
            onClick={() => {
              if (!user) {
                onAuthRequired();
              } else {
                onReply(comment.id);
              }
            }}
            className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            Reply
          </button>
        )}
      </div>

      {/* Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="ml-6 pt-3 border-l-2 border-muted pl-4 space-y-4">
          {comment.replies.map((reply) => (
            <CommentItem 
              key={reply.id} 
              comment={reply} 
              onReply={onReply}
              blogPostId={blogPostId}
              onAuthRequired={onAuthRequired}
              isNestedReply={true}
              lastViewTime={lastViewTime}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CommentSection = ({ blogPostId }: CommentSectionProps) => {
  const { user } = useAuth();
  const { isSubscribed, toggleSubscription, loading: subscriptionsLoading } = useSubscriptions(user?.id);
  const { lastViewTime } = useViewCount(blogPostId);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const observerRef = useRef<HTMLDivElement>(null);
  const COMMENTS_PER_PAGE = 10;

  const fetchComments = useCallback(async (currentOffset: number, append = false) => {
    try {
      const { data, error } = await supabase
        .rpc('get_public_comments', { post_id: blogPostId });

      if (error) throw error;
      
      // Organize comments into a tree structure
      const commentMap = new Map<string, Comment>();
      const allRootComments: Comment[] = [];
      
      // First pass: create all comment objects
      data?.forEach(comment => {
        commentMap.set(comment.id, { ...comment, replies: [] });
      });
      
      // Second pass: organize into tree structure
      data?.forEach(comment => {
        const commentObj = commentMap.get(comment.id)!;
        if (comment.parent_comment_id) {
          const parent = commentMap.get(comment.parent_comment_id);
          if (parent) {
            parent.replies = parent.replies || [];
            parent.replies.push(commentObj);
          }
        } else {
          allRootComments.push(commentObj);
        }
      });
      
      // Apply pagination to root comments only
      const paginatedComments = allRootComments.slice(currentOffset, currentOffset + COMMENTS_PER_PAGE);
      
      setComments(prev => append ? [...prev, ...paginatedComments] : paginatedComments);
      setHasMore(currentOffset + COMMENTS_PER_PAGE < allRootComments.length);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [blogPostId]);

  useEffect(() => {
    setComments([]);
    setOffset(0);
    setHasMore(true);
    fetchComments(0, false);
  }, [blogPostId, fetchComments]);

  // Scroll to comment from URL hash
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith('#comment-')) {
      const commentId = hash.replace('#comment-', '');
      // Wait for comments to load and DOM to render
      setTimeout(() => {
        const element = document.getElementById(`comment-${commentId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Highlight the comment briefly
          element.classList.add('ring-2', 'ring-primary', 'rounded-lg', 'transition-all');
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-primary');
          }, 2000);
        }
      }, 500);
    }
  }, [comments]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          const newOffset = offset + COMMENTS_PER_PAGE;
          setOffset(newOffset);
          fetchComments(newOffset, true);
        }
      },
      { threshold: 0.1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loading, offset, fetchComments]);

  const handleSubmit = async (e: React.FormEvent, parentId?: string) => {
    e.preventDefault();

    if (!user) {
      toast.error('Please sign in to comment');
      return;
    }

    const content = parentId ? replyContent : newComment;
    setSubmitting(true);

    try {
      const validatedContent = sanitizeText(content);

      if (validatedContent.length < 1 || validatedContent.length > 5000) {
        toast.error('Comment must be between 1 and 5000 characters');
        return;
      }

      const commentData = {
        blog_post_id: blogPostId,
        content: validatedContent,
        user_id: user.id,
        parent_comment_id: parentId || null,
      };

      const { error } = await supabase
        .from('comments')
        .insert([commentData]);

      if (error) throw error;

      toast.success(parentId ? 'Reply added!' : 'Comment added!');

      if (parentId) {
        setReplyContent('');
        setReplyingTo(null);
      } else {
        setNewComment('');
      }

      // Reset and refetch from beginning
      setComments([]);
      setOffset(0);
      setHasMore(true);
      fetchComments(0, false);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = (commentId: string) => {
    setReplyingTo(commentId);
  };

  const handleUnauthenticatedAction = () => {
    setIsAuthModalOpen(true);
  };

  const handleCommentClick = async (e: React.FormEvent, parentId?: string) => {
    e.preventDefault();
    
    if (!user) {
      setIsAuthModalOpen(true);
      return;
    }
    
    handleSubmit(e, parentId);
  };


  if (loading) {
    return <div className="text-center">Loading comments...</div>;
  }

  return (
    <div id="comments-section" className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold">Comments ({comments.length})</h3>
        {user && (
          <Button
            size="sm"
            variant="ghost"
            onClick={async () => {
              await toggleSubscription('comment_reply', blogPostId);
            }}
            disabled={subscriptionsLoading}
            className="gap-2"
          >
            {isSubscribed('comment_reply', blogPostId) ? (
              <>
                <span className="hidden sm:inline">Unfollow Replies</span>
                <span className="sm:hidden">Unfollow</span>
                <BellOff className="w-4 h-4" />
              </>
            ) : (
              <>
                <span className="hidden sm:inline">Follow Replies</span>
                <span className="sm:hidden">Follow</span>
                <Bell className="w-4 h-4" />
              </>
            )}
          </Button>
        )}
      </div>
      
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleCommentClick} className="space-y-4">
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Share your thoughts..."
              rows={4}
              onClick={() => !user && setIsAuthModalOpen(true)}
              onFocus={() => !user && setIsAuthModalOpen(true)}
              required={user !== null}
            />
            <div className="flex items-center justify-between">
              <Button 
                type="submit" 
                disabled={submitting && user !== null}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                {submitting ? 'Posting...' : 'Post Comment'}
              </Button>
              {user && isSubscribed('comment_reply', blogPostId) && (
                <span className="text-xs text-muted-foreground">
                  ✓ You'll be notified of replies
                </span>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {comments.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            No comments yet. Be the first to share your thoughts!
          </p>
        ) : (
          comments.map((comment) => (
            <Card key={comment.id} className="space-y-4">
              <CardContent className="pt-4">
                <CommentItem 
                  comment={comment} 
                  onReply={handleReply}
                  blogPostId={blogPostId}
                  onAuthRequired={handleUnauthenticatedAction}
                  isNestedReply={false}
                  lastViewTime={lastViewTime}
                />
                
                {/* Reply form */}
                {replyingTo === comment.id && user && (
                  <form onSubmit={(e) => handleSubmit(e, comment.id)} className="mt-4 space-y-4 border-t pt-4">
                    <Textarea
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      placeholder="Write your reply..."
                      rows={3}
                      required
                    />
                    <div className="flex gap-2">
                      <Button type="submit" disabled={submitting} size="sm">
                        {submitting ? 'Posting...' : 'Post Reply'}
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setReplyingTo(null);
                          setReplyContent('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
          ))
        )}
        
        {/* Infinite scroll trigger */}
        {hasMore && <div ref={observerRef} className="h-10 flex items-center justify-center">
          <p className="text-sm text-muted-foreground">Loading more comments...</p>
        </div>}
      </div>

      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
    </div>
  );
};

export default CommentSection;